import React from "react";
import { Controller } from "react-hook-form";
import { theme } from "@src/theme";
import {
  ToggleSwitchContainer,
  ToggleInput,
  ToggleSlider,
  CircularToggleContainer,
  CircularToggleButton,
  CircularToggleText,
  InlineToggleWrapper,
  InlineToggleLabel,
} from "./FormToggleSwitch.styles";

/**
 * Props for the FormToggleSwitch component.
 */
interface FormToggleSwitchProps {
  /**
   * The name of the field. Required for react-hook-form integration.
   */
  name: string;
  /**
   * The form control object from useForm().
   */
  control: any;
  /**
   * The label text displayed above the toggle switch.
   */
  label: string;
  /**
   * Optional custom styles for the container.
   */
  containerStyle?: React.CSSProperties;
  /**
   * Optional callback when the toggle is changed.
   */
  onChange?: (checked: boolean) => void;
  /**
   * Visual style for the toggle control.
   */
  variant?: "default" | "circular";
}

/**
 * A shared toggle switch component integrated with react-hook-form.
 * This ensures DRY principles by removing repetitive Controller boilerplate.
 *
 * @param {FormToggleSwitchProps} props - The component props.
 * @returns {React.JSX.Element} The rendered FormToggleSwitch component.
 */
const FormToggleSwitch: React.FC<FormToggleSwitchProps> = ({
  name,
  control,
  label,
  containerStyle,
  onChange,
  variant = "default",
}) => {
  if (variant === "circular") {
    return (
      <Controller
        name={name}
        control={control}
        render={({ field }) => {
          const checked = field.value || false;

          return (
            <InlineToggleWrapper style={containerStyle}>
              <InlineToggleLabel htmlFor={name}>{label}</InlineToggleLabel>
              <CircularToggleContainer htmlFor={name}>
                <ToggleInput
                  id={name}
                  type="checkbox"
                  onChange={(e) => {
                    field.onChange(e.target.checked);
                    onChange?.(e.target.checked);
                  }}
                  checked={checked}
                />
                <CircularToggleButton $checked={checked} aria-hidden="true">
                  <CircularToggleText $visible={checked} $isOn>
                    ON
                  </CircularToggleText>
                  <CircularToggleText $visible={!checked} $isOn={false}>
                    OFF
                  </CircularToggleText>
                </CircularToggleButton>
              </CircularToggleContainer>
            </InlineToggleWrapper>
          );
        }}
      />
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        ...containerStyle,
      }}
    >
      <label
        htmlFor={name}
        style={{
          color: theme.colors.yellow,
          cursor: "pointer",
          fontWeight: "bold",
        }}
      >
        {label}
      </label>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <ToggleSwitchContainer style={{ marginTop: "5px" }}>
            <ToggleInput
              id={name}
              type="checkbox"
              onChange={(e) => {
                field.onChange(e.target.checked);
                if (onChange) onChange(e.target.checked);
              }}
              checked={field.value || false}
            />
            <ToggleSlider $checked={field.value || false} />
          </ToggleSwitchContainer>
        )}
      />
    </div>
  );
};

export default FormToggleSwitch;
